import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    Plus, Loader2, MessageSquareQuote, Search, Filter, CheckCircle2, XCircle,
    Clock, Eye, Pencil, Trash2, Send, Video, FileText, Code, MoreHorizontal,
    Star, ChevronDown, Users
} from 'lucide-react';
import TestimonialCard from '../components/testimonials/TestimonialCard';
import EmbedCodeModal from '../components/testimonials/EmbedCodeModal';
import StarRating from '../components/testimonials/StarRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---- Send Modal ---- */
function SendModal({ requestId, onClose, onSent, getAuthHeader }) {
    const [search, setSearch] = useState('');
    const [contacts, setContacts] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (!search.trim()) { setContacts([]); return; }
            setLoading(true);
            try {
                const res = await axios.get(`${API}/contacts?search=${encodeURIComponent(search)}&page_size=10`, { headers: getAuthHeader() });
                const data = Array.isArray(res.data) ? res.data : res.data.data || [];
                setContacts(data);
            } catch { setContacts([]); }
            finally { setLoading(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [search, getAuthHeader]);

    const toggleContact = (c) => {
        setSelected(prev => prev.find(s => s.contact_id === c.contact_id) ? prev.filter(s => s.contact_id !== c.contact_id) : [...prev, c]);
    };

    const handleSend = async () => {
        if (!selected.length) { toast.error('Select at least one contact'); return; }
        setSending(true);
        try {
            await axios.post(`${API}/testimonials/${requestId}/send`, {
                contact_ids: selected.map(c => c.contact_id),
            }, { headers: getAuthHeader() });
            toast.success(`Sent to ${selected.length} contact${selected.length > 1 ? 's' : ''}`);
            onSent();
            onClose();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
        finally { setSending(false); }
    };

    // `!mt-0`: mounted as a later child of the page root's `space-y-6`, whose
    // margin would push this fixed overlay 24px below the viewport top (#496).
    return (
        <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-stone-200">
                    <h2 className="text-base font-semibold text-stone-900">Send Testimonial Request</h2>
                    <p className="text-xs text-stone-500 mt-0.5">Search and select contacts to send to</p>
                </div>
                <div className="p-5 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            placeholder="Search contacts..." />
                    </div>

                    {/* Selected chips */}
                    {selected.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {selected.map(c => (
                                <span key={c.contact_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-xs">
                                    {c.first_name} {c.last_name}
                                    <button onClick={() => toggleContact(c)} className="hover:text-red-500">
                                        <XCircle className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Results */}
                    <div className="max-h-48 overflow-y-auto space-y-1">
                        {loading && <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto text-stone-400" /></div>}
                        {contacts.map(c => {
                            const isSelected = selected.some(s => s.contact_id === c.contact_id);
                            return (
                                <button key={c.contact_id} onClick={() => toggleContact(c)}
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                        isSelected ? 'bg-stone-900 text-white' : 'hover:bg-stone-50'
                                    }`}>
                                    <Users className="w-4 h-4 flex-shrink-0" />
                                    <span className="flex-1">{c.first_name} {c.last_name}</span>
                                    <span className={`text-xs ${isSelected ? 'text-white/60' : 'text-stone-400'}`}>{c.email}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-stone-200 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                    <Button size="sm" onClick={handleSend} disabled={sending || !selected.length}>
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                        Send ({selected.length})
                    </Button>
                </div>
            </div>
        </div>
    );
}

/* ---- Submission Detail Modal ---- */
function SubmissionDetail({ submission, onClose, onStatusChange, getAuthHeader }) {
    const [embedModal, setEmbedModal] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);

    useEffect(() => {
        if (submission?.video_url) setVideoUrl(submission.video_url);
    }, [submission]);

    if (!submission) return null;

    const updateStatus = async (status) => {
        try {
            await axios.put(`${API}/testimonials/submissions/${submission.submission_id}/status`,
                { status }, { headers: getAuthHeader() });
            toast.success(`Testimonial ${status}`);
            onStatusChange();
        } catch { toast.error('Failed to update status'); }
    };

    // `!mt-0`: the fragment's children land in the page root's `space-y-6`,
    // whose margin would push each fixed overlay 24px below the viewport top (#496).
    return (
        <>
            <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
                <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                        <div>
                            <h2 className="text-base font-semibold text-stone-900">{submission.contact_name || 'Testimonial'}</h2>
                            <p className="text-xs text-stone-500">From {submission.request_name} &middot; Sent by {submission.sent_by_name}</p>
                        </div>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100">
                            <XCircle className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="p-5 space-y-4">
                        {/* Video */}
                        {videoUrl && (
                            <div className="rounded-xl overflow-hidden bg-stone-900">
                                <video src={videoUrl} controls playsInline className="w-full" />
                            </div>
                        )}

                        {/* Rating */}
                        {submission.overall_rating > 0 && (
                            <div>
                                <label className="text-xs font-medium text-stone-500 mb-1 block">Rating</label>
                                <StarRating value={submission.overall_rating} maxStars={5} size="md" readonly />
                            </div>
                        )}

                        {/* Answers */}
                        {submission.answers && Object.entries(submission.answers).map(([key, val]) => (
                            typeof val === 'string' && val.trim() && (
                                <div key={key}>
                                    <label className="text-xs font-medium text-stone-500 mb-1 block">{key}</label>
                                    <p className="text-sm text-stone-800 bg-stone-50 rounded-lg p-3">{val}</p>
                                </div>
                            )
                        ))}

                        {/* Status */}
                        <div className="text-xs text-stone-400">
                            Status: <span className="font-medium text-stone-600">{submission.status}</span>
                            {submission.submitted_at && <> &middot; Submitted {new Date(submission.submitted_at).toLocaleString()}</>}
                        </div>
                    </div>

                    <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-between">
                        <div className="flex gap-2">
                            {submission.status !== 'approved' && (
                                <Button size="sm" onClick={() => updateStatus('approved')}>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                </Button>
                            )}
                            {submission.status !== 'rejected' && (
                                <Button variant="outline" size="sm" onClick={() => updateStatus('rejected')}>
                                    <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setEmbedModal(true)}>
                            <Code className="w-4 h-4 mr-1" /> Embed
                        </Button>
                    </div>
                </div>
            </div>
            {embedModal && <EmbedCodeModal submission={submission} onClose={() => setEmbedModal(false)} />}
        </>
    );
}

/* ======== Main Page ======== */
export default function Testimonials() {
    const navigate = useNavigate();
    const { getAuthHeader, user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [tab, setTab] = useState('requests');
    const [requests, setRequests] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sendModalRequest, setSendModalRequest] = useState(null);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const loadRequests = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/testimonials`, { headers: getAuthHeader() });
            setRequests(Array.isArray(res.data) ? res.data : res.data.data || []);
        } catch { toast.error('Failed to load testimonial requests'); }
    }, [getAuthHeader]);

    const loadSubmissions = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (typeFilter) params.set('type', typeFilter);
            if (search) params.set('search', search);
            const res = await axios.get(`${API}/testimonials/submissions?${params}`, { headers: getAuthHeader() });
            setSubmissions(Array.isArray(res.data) ? res.data : res.data.data || []);
        } catch { toast.error('Failed to load submissions'); }
    }, [getAuthHeader, statusFilter, typeFilter, search]);

    useEffect(() => {
        Promise.all([loadRequests(), loadSubmissions()]).finally(() => setLoading(false));
    }, [loadRequests, loadSubmissions]);

    const deleteRequest = async (id) => {
        if (!window.confirm('Delete this request and all its submissions?')) return;
        try {
            await axios.delete(`${API}/testimonials/${id}`, { headers: getAuthHeader() });
            toast.success('Deleted');
            loadRequests();
            loadSubmissions();
        } catch { toast.error('Failed to delete'); }
    };

    const bulkUpdateStatus = async (status) => {
        if (!selectedIds.length) return;
        try {
            await axios.put(`${API}/testimonials/submissions/bulk-status`, {
                submission_ids: selectedIds, status,
            }, { headers: getAuthHeader() });
            toast.success(`${selectedIds.length} testimonial(s) ${status}`);
            setSelectedIds([]);
            loadSubmissions();
        } catch { toast.error('Failed to update'); }
    };

    const openSubmission = async (sub) => {
        try {
            const res = await axios.get(`${API}/testimonials/submissions/${sub.submission_id}`, { headers: getAuthHeader() });
            setSelectedSubmission(res.data);
        } catch { toast.error('Failed to load submission'); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-96">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
                        <MessageSquareQuote className="w-6 h-6" /> Testimonials
                    </h1>
                    <p className="text-sm text-stone-500 mt-0.5">Collect, manage, and share customer testimonials</p>
                </div>
                <Button onClick={() => navigate('/testimonials/new/edit')}>
                    <Plus className="w-4 h-4 mr-1" /> New Request
                </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-stone-200">
                {[
                    { key: 'requests', label: 'Requests', count: requests.length },
                    { key: 'submissions', label: 'Submissions', count: submissions.length },
                ].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                            tab === t.key ? 'border-stone-900 text-stone-900 font-medium' : 'border-transparent text-stone-500 hover:text-stone-700'
                        }`}>
                        {t.label} <span className="text-xs text-stone-400 ml-1">({t.count})</span>
                    </button>
                ))}
            </div>

            {/* Requests Tab */}
            {tab === 'requests' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {requests.map(r => (
                        <div key={r.request_id} className="bg-white border border-stone-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-semibold text-stone-900 text-sm">{r.name}</h3>
                                    {r.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{r.description}</p>}
                                </div>
                                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                                    {r.is_active !== false ? 'Active' : 'Inactive'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-stone-400 mb-3">
                                <span>{r.pages?.length || 0} page{(r.pages?.length || 0) !== 1 ? 's' : ''}</span>
                                <span>{r.submission_count || 0} submission{(r.submission_count || 0) !== 1 ? 's' : ''}</span>
                                {r.is_template && <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-500">Template</span>}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => navigate(`/testimonials/${r.request_id}/edit`)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-stone-100 hover:bg-stone-200 rounded-lg text-stone-700">
                                    <Pencil className="w-3 h-3" /> Edit
                                </button>
                                <button onClick={() => setSendModalRequest(r.request_id)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-stone-900 hover:bg-stone-800 rounded-lg text-white">
                                    <Send className="w-3 h-3" /> Send
                                </button>
                                {isAdmin && (
                                    <button onClick={() => deleteRequest(r.request_id)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs hover:bg-red-50 rounded-lg text-red-500 ml-auto">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    {requests.length === 0 && (
                        <div className="col-span-full text-center py-16">
                            <MessageSquareQuote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                            <h3 className="text-sm font-medium text-stone-600 mb-1">No testimonial requests yet</h3>
                            <p className="text-xs text-stone-400 mb-4">Create your first request to start collecting testimonials</p>
                            <Button size="sm" onClick={() => navigate('/testimonials/new/edit')}>
                                <Plus className="w-4 h-4 mr-1" /> Create Request
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Submissions Tab */}
            {tab === 'submissions' && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[200px] max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <input value={search} onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                placeholder="Search by name or email..." />
                        </div>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                            <option value="">All Types</option>
                            <option value="video">Video</option>
                            <option value="text">Text</option>
                            <option value="mixed">Mixed</option>
                        </select>

                        {/* Bulk actions */}
                        {selectedIds.length > 0 && isAdmin && (
                            <div className="flex items-center gap-2 ml-auto">
                                <span className="text-xs text-stone-500">{selectedIds.length} selected</span>
                                <Button size="sm" onClick={() => bulkUpdateStatus('approved')}>
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => bulkUpdateStatus('rejected')}>
                                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Submissions grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {submissions.map(sub => (
                            <div key={sub.submission_id} className="relative">
                                {isAdmin && (
                                    <div className="absolute top-2 left-2 z-10">
                                        <input type="checkbox" checked={selectedIds.includes(sub.submission_id)}
                                            onChange={e => {
                                                setSelectedIds(prev => e.target.checked
                                                    ? [...prev, sub.submission_id]
                                                    : prev.filter(id => id !== sub.submission_id));
                                            }}
                                            className="rounded border-stone-300" />
                                    </div>
                                )}
                                <TestimonialCard submission={sub} onClick={() => openSubmission(sub)} />
                            </div>
                        ))}
                    </div>

                    {submissions.length === 0 && (
                        <div className="text-center py-16">
                            <MessageSquareQuote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                            <h3 className="text-sm font-medium text-stone-600 mb-1">No submissions yet</h3>
                            <p className="text-xs text-stone-400">Send testimonial requests to start collecting feedback</p>
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            {sendModalRequest && (
                <SendModal requestId={sendModalRequest} onClose={() => setSendModalRequest(null)}
                    onSent={() => { loadRequests(); loadSubmissions(); }} getAuthHeader={getAuthHeader} />
            )}
            {selectedSubmission && (
                <SubmissionDetail submission={selectedSubmission} onClose={() => setSelectedSubmission(null)}
                    onStatusChange={() => { loadSubmissions(); setSelectedSubmission(null); }}
                    getAuthHeader={getAuthHeader} />
            )}
        </div>
    );
}
